import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Popover,
  Button,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import alertify from 'alertifyjs';
import ZoomControlBar from '../common/ZoomControlBar';
import { useHasPermission } from '../../hooks/usePermission';
import { getSettingValue } from '../../redux/slices/appSettingSlice';
import '../../components/common/RichTextEditor.css';
import './DocumentPreview.css';

const DocumentPreview = ({ content = '', answers = {}, questions = [], questionTree = null, onRender = null, status = 'draft', creditsUsed = 0, skipApiCalls = false, showOptionalPlaceholders = false }) => {

  const dispatch = useDispatch();
  const [debouncedContent, setDebouncedContent] = useState(content);
  const [debouncedAnswers, setDebouncedAnswers] = useState(answers);
  const [zoom, setZoom] = useState(100);
  const [viewMode, setViewMode] = useState('actual-size');
  const [selectedText, setSelectedText] = useState('');
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [researchTextMaxLength, setResearchTextMaxLength] = useState(250); // Default değer

  // Programatik kopyalama flag'i (copy protection'ı bypass etmek için)
  const isProgrammaticCopyRef = useRef(false);

  // Permission kontrolü
  const hasUnrestrictedAccess = useHasPermission('documents.preview.unrestricted');
  
  // Blur ve copy protection kontrolü
  const isDraftMode = status === 'draft';
  const hasCreditsPaid = status === 'completed' && creditsUsed > 0;
  // Blur: draft mode'da, unrestricted access yoksa ve credits ödenmemişse aktif
  const shouldBlur = isDraftMode && !hasUnrestrictedAccess && !hasCreditsPaid;
  const shouldBlockCopy = true; // Her durumda aktif

  // AppSetting'ten research_text_max_length değerini yükle
  // skipApiCalls prop'u true ise API isteği yapılmayacak (public demo için)
  useEffect(() => {
    if (skipApiCalls) {
      // API isteği atlanıyor, default değer kullanılacak
      return;
    }

    const loadResearchTextMaxLength = async () => {
      try {
        const result = await dispatch(getSettingValue('research_text_max_length')).unwrap();
        const maxLength = parseInt(result?.value || '250', 10);
        setResearchTextMaxLength(maxLength);
      } catch (error) {
        console.error('Error fetching research_text_max_length:', error);
        // Default değer zaten 250, hata durumunda da 250 kullanılacak
      }
    };

    loadResearchTextMaxLength();
  }, [dispatch, skipApiCalls]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContent(content);
      setDebouncedAnswers(answers);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [content, answers]);
  
  const renderContentRef = useRef(null);

  const allQuestions = useMemo(() => {
    const flatten = (nodes) => {
      const result = [];
      nodes.forEach((node) => {
        result.push(node);
        if (node.children && Array.isArray(node.children)) {
          result.push(...flatten(node.children));
        }
        if (node.answers && Array.isArray(node.answers)) {
          node.answers.forEach((answer) => {
            if (answer.content?.nestedQuestions && Array.isArray(answer.content.nestedQuestions)) {
              answer.content.nestedQuestions.forEach((nestedQ) => {
                if (typeof nestedQ === 'object' && nestedQ !== null && !nestedQ._id) {
                  result.push(nestedQ);
                  if (nestedQ.children && Array.isArray(nestedQ.children)) {
                    result.push(...flatten(nestedQ.children));
                  }
                  if (nestedQ.answers && Array.isArray(nestedQ.answers)) {
                    nestedQ.answers.forEach((nestedAnswer) => {
                      if (nestedAnswer.content?.nestedQuestions && Array.isArray(nestedAnswer.content.nestedQuestions)) {
                        result.push(...flatten(nestedAnswer.content.nestedQuestions));
                      }
                    });
                  }
                } else if (typeof nestedQ === 'object' && nestedQ !== null) {
                  result.push(nestedQ);
                  if (nestedQ.children && Array.isArray(nestedQ.children)) {
                    result.push(...flatten(nestedQ.children));
                  }
                  if (nestedQ.answers && Array.isArray(nestedQ.answers)) {
                    nestedQ.answers.forEach((nestedAnswer) => {
                      if (nestedAnswer.content?.nestedQuestions && Array.isArray(nestedAnswer.content.nestedQuestions)) {
                        result.push(...flatten(nestedAnswer.content.nestedQuestions));
                      }
                    });
                  }
                }
              });
            }
          });
        }
      });
      return result;
    };

    if (questions && Array.isArray(questions) && questions.length > 0) {
      return questions;
    }
    
    if (questionTree && Array.isArray(questionTree) && questionTree.length > 0) {
      return flatten(questionTree);
    }
    
    return [];
  }, [questions, questionTree]);
  
  const renderRepeatableField = useCallback((question, value, questionsList, result, key, currentAnswers, renderContentFn, depth = 0, showOptional = false) => {
    if (!Array.isArray(value) || value.length === 0) {
      return result.replaceAll(`{{${key}}}`, '');
    }
    
    const listType = question.listType || 'ordered';
    const items = value.map((item, idx) => {
      const itemAnswers = {};
      Object.keys(currentAnswers).forEach((answerKey) => {
        if (answerKey.startsWith(`${key}_${idx}_`)) {
          const nestedKey = answerKey.replace(`${key}_${idx}_`, '');
          itemAnswers[nestedKey] = currentAnswers[answerKey];
        }
      });
      
      // CRITICAL: Add default values for nested questions that don't have values yet
      // This ensures default values are shown in preview for repeatable nested questions
      if (question.answers && Array.isArray(question.answers)) {
        const selectedAnswer = question.answers.find((a) => a.value === item);
        if (selectedAnswer && selectedAnswer.content && selectedAnswer.content.nestedQuestions) {
          selectedAnswer.content.nestedQuestions.forEach((nestedQ) => {
            if (nestedQ && nestedQ.key) {
              // Only add defaultValue if value is truly undefined/null (not set by user)
              if (itemAnswers[nestedQ.key] === undefined || itemAnswers[nestedQ.key] === null) {
                if (nestedQ.defaultValue && nestedQ.defaultValue.trim() !== '') {
                  if (nestedQ.type === 'Checkbox') {
                    // For Checkbox: defaultValue is comma-separated string, convert to array
                    itemAnswers[nestedQ.key] = nestedQ.defaultValue.split(',').map(v => v.trim()).filter(Boolean);
                  } else {
                    itemAnswers[nestedQ.key] = nestedQ.defaultValue;
                  }
                }
              }
            }
          });
        }
      }
      
      let itemContent = String(item);
      if (question.answers && Array.isArray(question.answers)) {
        const selectedAnswer = question.answers.find((a) => a.value === item);
        if (selectedAnswer && selectedAnswer.content && selectedAnswer.content.paragraph) {
          itemContent = selectedAnswer.content.paragraph;
          const mergedAnswers = { ...currentAnswers, ...itemAnswers };
          itemContent = renderContentFn(itemContent, mergedAnswers, questionsList, depth, showOptional);
        } else {
          itemContent = selectedAnswer?.displayText || String(item);
        }
      }
      
      // For TextArea type, preserve newlines by converting \n to <br>
      if (question.type === 'TextArea') {
        itemContent = itemContent.replace(/\n/g, '<br>');
      }
      
      return itemContent;
    });
    
    if (listType === 'none') {
      const itemsHtml = items.map((itemContent) => `<p>${itemContent}</p>`).join('');
      return result.replaceAll(`{{${key}}}`, itemsHtml);
    } else {
      const listTag = listType === 'unordered' ? 'ul' : 'ol';
      const listItems = items.map((itemContent) => `<li>${itemContent}</li>`).join('');
      const finalHtml = `<${listTag}>${listItems}</${listTag}>`;
      
      return result.replaceAll(`{{${key}}}`, finalHtml);
    }
  }, []);
  
  // Helper function to format placeholder text
  // MUST be defined before renderAnswerBranch and formatLocationValue
  const formatPlaceholder = useCallback((question, key, showOptional = false) => {
    // Zorunlu olmayan sorular için placeholder gösterme (showOptional true ise göster)
    if (question && question.required === false && !showOptional) {
      return '';
    }
    // Use displayText if question exists, otherwise format key
    const displayText = question?.displayText || key.replace(/_/g, ' ');
    // Return HTML span with CSS class for styling
    return `<span class="document-preview-placeholder">${displayText}</span>`;
  }, []);

  const renderAnswerBranch = useCallback((question, value, currentAnswers, questionsList, result, key, renderContentFn, depth = 0, showOptional = false) => {
    // CRITICAL: Empty string means user cleared the value - show placeholder
    // Only show empty if value is truly undefined/null (never set by user)
    if (value === null || value === undefined) {
      // Value was never set - show placeholder (only if required or showOptional is true)
      if (question.required !== false || showOptional) {
        return result.replaceAll(`{{${key}}}`, formatPlaceholder(question, key, showOptional));
      }
      return result.replaceAll(`{{${key}}}`, '');
    }
    
    if (value === '') {
      // User explicitly cleared the value - show placeholder (only if required or showOptional is true)
      if (question.required !== false || showOptional) {
        return result.replaceAll(`{{${key}}}`, formatPlaceholder(question, key, showOptional));
      }
      return result.replaceAll(`{{${key}}}`, '');
    }
    
    const selectedAnswer = question.answers.find((a) => a.value === value);
    
    if (selectedAnswer && selectedAnswer.content && selectedAnswer.content.paragraph) {
      let answerHtml = selectedAnswer.content.paragraph || '';
      answerHtml = renderContentFn(answerHtml, currentAnswers, questionsList, depth, showOptional);
      return result.replaceAll(`{{${key}}}`, answerHtml);
    } else {
      // Answer not found or no paragraph - show placeholder (only if required or showOptional is true)
      if (question.required !== false || showOptional) {
        return result.replaceAll(`{{${key}}}`, formatPlaceholder(question, key, showOptional));
      }
      return result.replaceAll(`{{${key}}}`, '');
    }
  }, [formatPlaceholder]);
  
  const formatLocationValue = useCallback((rawValue, key, question = null, showOptional = false) => {
    if (!rawValue) return formatPlaceholder(question, key, showOptional);
  
    let locationValue = rawValue;
    try {
      if (typeof rawValue === 'string') {
        locationValue = JSON.parse(rawValue);
      }
    } catch (e) {
      return formatPlaceholder(question, key, showOptional);
    }
  
    if (!locationValue || typeof locationValue !== 'object') {
      return formatPlaceholder(question, key, showOptional);
    }
  
    const parts = [];
  
    const pushPart = (part) => {
      if (!part) return;
      if (typeof part === 'string') parts.push(part);
      else if (part.name) parts.push(part.name);
    };
  
    pushPart(locationValue.il);
    pushPart(locationValue.ilce);
    pushPart(locationValue.mahalle);
  
    return parts.length > 0 ? parts.join(' > ') : formatPlaceholder(question, key, showOptional);
  }, [formatPlaceholder]);
  
  const renderContent = useCallback((contentHtml, currentAnswers, questionsList, depth = 0, showOptional = false) => {
    if (!contentHtml || typeof contentHtml !== 'string') return '';
    if (depth > 10) {
      console.warn('[DocumentPreview] Maximum recursion depth reached');
      return contentHtml;
    }
    
    let result = contentHtml;
    
    const placeholderRegex = /\{\{([A-Za-z][A-Za-z0-9_]*)\}\}/g;
    const placeholders = new Set();
    let match;
    placeholderRegex.lastIndex = 0;
    while ((match = placeholderRegex.exec(contentHtml)) !== null) {
      placeholders.add(match[1]);
    }
    
    for (const key of placeholders) {
      if (/^\w+_\d+_\w+$/.test(key)) {
        continue;
      }
      
      const value = currentAnswers[key];
      const question = questionsList.find((q) => q && q.key === key);
      
      if (!question) {
        if (value !== null && value !== undefined && value !== '') {
          let displayValue;
      
          // Soru bulunamasa bile LocationSelector formatına benzeyen obje/string ise düzgün göster
          if (
            (typeof value === 'string' && value.includes('"il"')) ||
            (typeof value === 'object' && value !== null &&
              ('il' in value || 'ilce' in value || 'mahalle' in value))
          ) {
            displayValue = formatLocationValue(value, key, null, showOptional);
          } else {
            displayValue = String(value);
          }
      
          result = result.replaceAll(`{{${key}}}`, displayValue);
        } else {
          result = result.replaceAll(`{{${key}}}`, formatPlaceholder(null, key, showOptional));
        }
        continue;
      }
      
      
      if (question.repeatable && Array.isArray(value)) {
        result = renderRepeatableField(question, value, questionsList, result, key, currentAnswers, renderContent, depth + 1, showOptional);
      }
      else if (
        (question.type === 'RadioButton' || question.type === 'DropDown') &&
        question.answers &&
        question.answers.length > 0
      ) {
        result = renderAnswerBranch(question, value, currentAnswers, questionsList, result, key, renderContent, depth + 1, showOptional);
      }
      else {
        let displayValue = value;
        // CRITICAL: Empty string means user cleared the value, don't use defaultValue
        // Only use defaultValue if value is truly undefined/null (never set by user)
        if (value === null || value === undefined) {
          // Value was never set - use defaultValue if available
          if (question.defaultValue && question.defaultValue.trim() !== '') {
            displayValue = question.defaultValue;
          } else {
            // Show placeholder only if required or showOptional is true
            if (question.required !== false || showOptional) {
              displayValue = formatPlaceholder(question, key, showOptional);
            } else {
              displayValue = '';
            }
          }
        } else if (value === '') {
          // User explicitly cleared the value - show placeholder only if required or showOptional is true
          if (question.required !== false || showOptional) {
            displayValue = formatPlaceholder(question, key, showOptional);
          } else {
            displayValue = '';
          }
        } else if (question.type === 'LocationSelector') {
          // Ortak helper
          displayValue = formatLocationValue(value, key, question, showOptional);
        } else if (question.type === 'Signature' && typeof value === 'string' && value.startsWith('data:image')) {
          // Signature: Render as base64 image with appropriate sizing for document
          displayValue = `<img src="${value}" alt="İmza" class="document-preview-signature" />`;
        } else if (
          typeof value === 'object' &&
          !Array.isArray(value) &&
          (value.il || value.ilce || value.mahalle)
        ) {
          // Tip yanlış ya da question yanlış flatten edilmiş olsa bile,
          // LocationSelector cevabı gibi görünen objeleri de düzgün göster
          displayValue = formatLocationValue(value, key, question, showOptional);
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          displayValue = JSON.stringify(value);
        } else {
          displayValue = String(value);
        }
        
        
        // For TextArea type, preserve newlines by converting \n to <br>
        if (question.type === 'TextArea') {
          displayValue = displayValue.replace(/\n/g, '<br>');
        }
        
        result = result.replaceAll(`{{${key}}}`, displayValue);
      }
    }
    
    // Replace any remaining unmatched placeholders with formatted placeholder
    result = result.replace(/\{\{([A-Za-z][A-Za-z0-9_]*)\}\}/g, (match, key) => {
      const question = questionsList.find((q) => q && q.key === key);
      return formatPlaceholder(question, key, showOptional);
    });
    
    return result;
  }, [renderRepeatableField, renderAnswerBranch, formatLocationValue, formatPlaceholder]);
  
  renderContentRef.current = renderContent;
  
  const renderedHtml = useMemo(() => {
    if (!renderContentRef.current) return '';
    return renderContentRef.current(debouncedContent, debouncedAnswers, allQuestions, 0, showOptionalPlaceholders);
  }, [debouncedContent, debouncedAnswers, allQuestions, showOptionalPlaceholders]);

  const copyToClipboard = useCallback(async (text) => {
    try {
      // Programatik kopyalama flag'ini set et
      isProgrammaticCopyRef.current = true;
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        // Flag'i kısa bir süre sonra sıfırla (event propagation için)
        setTimeout(() => {
          isProgrammaticCopyRef.current = false;
        }, 100);
        return true;
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          // Flag'i kısa bir süre sonra sıfırla
          setTimeout(() => {
            isProgrammaticCopyRef.current = false;
          }, 100);
          return true;
        } catch (err) {
          isProgrammaticCopyRef.current = false;
          return false;
        } finally {
          if (document.body.contains(textArea)) {
            document.body.removeChild(textArea);
          }
        }
      }
    } catch (error) {
      isProgrammaticCopyRef.current = false;
      return false;
    }
  }, []);

  const openNewWindowWithSize = useCallback((url, width, height) => {
    const randomId = Math.floor(Math.random() * 1000000);
    const windowFeatures = `top=0,left=0,width=${width},height=${height},status=yes,toolbar=no,scrollbars=yes,menubar=no,location=no,resizable=yes`;
    const newWindow = window.open(url, `NewWindow_${randomId}`, windowFeatures);
    if (newWindow) {
      newWindow.moveTo(0, 0);
      newWindow.focus();
    }
  }, []);

  const handleOpenPage = useCallback(async (type) => {
    if (!selectedText || selectedText.trim().length === 0) {
      return;
    }

    const success = await copyToClipboard(selectedText);
    if (!success) {
      alertify.error('Metin kopyalanırken hata oluştu. Lütfen manuel olarak kopyalayınız.');
      return;
    }

    alertify.success('Metin panoya kopyalandı');

    switch (type) {
      case 'yargitay':
        openNewWindowWithSize('https://karararama.yargitay.gov.tr/', 1650, 900);
        break;
      case 'mevzuat':
        openNewWindowWithSize('https://www.mevzuat.gov.tr/', 1650, 900);
        break;
      default:
        return;
    }

    setPopoverOpen(false);
    setSelectedText('');
    setPopoverAnchor(null);
  }, [selectedText, copyToClipboard, openNewWindowWithSize]);

  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    const selectedTextValue = selection.toString().trim();

    if (!selectedTextValue || selectedTextValue.length < 2) {
      setPopoverOpen(false);
      setSelectedText('');
      setPopoverAnchor(null);
      return;
    }

    // Karakter limiti kontrolü (AppSetting'ten alınan değer)
    if (selectedTextValue.length > researchTextMaxLength) {
      alertify.warning(`Araştırma için maksimum ${researchTextMaxLength} karakter seçebilirsiniz.`);
      setPopoverOpen(false);
      setSelectedText('');
      setPopoverAnchor(null);
      window.getSelection().removeAllRanges();
      return;
    }

    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    if (!range) {
      setPopoverOpen(false);
      return;
    }

    const rect = range.getBoundingClientRect();

    const anchorPosition = {
      top: rect.top - 10,
      left: rect.left + rect.width / 2,
    };

    setSelectedText(selectedTextValue);
    setPopoverAnchor(anchorPosition);
    setPopoverOpen(true);
  }, [researchTextMaxLength]);

  const handleClosePopover = useCallback(() => {
    setPopoverOpen(false);
    setSelectedText('');
    setPopoverAnchor(null);
    window.getSelection().removeAllRanges();
  }, []);

  // Render edilmiş HTML'i parent component'e gönder (completeDocument için)
  useEffect(() => {
    if (onRender && renderedHtml) {
      onRender(renderedHtml);
    }
  }, [renderedHtml, onRender]);

  // Copy Protection Event Listeners - Copy/cut işlemlerini engelle
  useEffect(() => {
    if (!shouldBlockCopy) return;

    const handleCopy = (e) => {
      // Programatik kopyalama ise engelleme
      if (isProgrammaticCopyRef.current) {
        return; // Event'i engelleme, normal akışa izin ver
      }
      
      // Sadece document-preview-content içindeki elementlerde engelle
      const target = e.target;
      if (target.closest('.document-preview-content')) {
        e.preventDefault();
        e.clipboardData?.setData('text/plain', '');
        alertify.warning('İçerik kopyalanamaz. Dokümanı tamamladıktan sonra PDF olarak indirebilirsiniz.');
        return false;
      }
    };

    const handleCut = (e) => {
      // Programatik kopyalama ise engelleme
      if (isProgrammaticCopyRef.current) {
        return;
      }
      
      const target = e.target;
      if (target.closest('.document-preview-content')) {
        e.preventDefault();
        e.clipboardData?.setData('text/plain', '');
        return false;
      }
    };

    const handleContextMenu = (e) => {
      // Sadece document-preview-content içindeki elementlerde engelle
      if (e.target.closest('.document-preview-content')) {
        e.preventDefault();
        return false;
      }
    };

    // Ctrl+C, Ctrl+V, Ctrl+X gibi klavye kısayollarını engelle
    const handleKeyDown = (e) => {
      const target = e.target;
      if (target.closest('.document-preview-content')) {
        // Programatik kopyalama ise engelleme
        if (isProgrammaticCopyRef.current) {
          return;
        }
        
        // Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A (tümünü seç) engelle
        if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a')) {
          if (e.key === 'c' || e.key === 'x') {
            e.preventDefault();
            alertify.warning('İçerik kopyalanamaz. Dokümanı tamamladıktan sonra PDF olarak indirebilirsiniz.');
            return false;
          } else if (e.key === 'v') {
            e.preventDefault();
            return false;
          } else if (e.key === 'a') {
            // Tümünü seç'e izin ver (mevzuat/yargıtay için gerekli olabilir)
            // Ama copy'yi engellemeye devam ediyoruz
          }
        }
      }
    };

    document.addEventListener('copy', handleCopy, true);
    document.addEventListener('cut', handleCut, true);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('copy', handleCopy, true);
      document.removeEventListener('cut', handleCut, true);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [shouldBlockCopy]);

  // Metin seçimi event listener'ı ekle
  useEffect(() => {
    const contentElements = document.querySelectorAll('.document-preview-content');
    
    const handleMouseUp = (e) => {
      setTimeout(() => {
        handleTextSelection();
      }, 10);
    };

    contentElements.forEach((element) => {
      element.addEventListener('mouseup', handleMouseUp);
    });

    return () => {
      contentElements.forEach((element) => {
        element.removeEventListener('mouseup', handleMouseUp);
      });
    };
  }, [handleTextSelection]);
  
  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 15, 250));
    setViewMode('custom');
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 15, 5));
    setViewMode('custom');
  };

  const handleZoomChange = (newValue) => {
    setZoom(newValue);
    setViewMode('custom');
  };

  const handleActualSize = () => {
    setViewMode('actual-size');
    setZoom(100);
  };

  const getZoomStyle = () => {
    if (viewMode === 'custom') {
      return {
        '--dp-zoom-scale': zoom / 100,
        '--rte-zoom-scale': zoom / 100
      };
    }
    return {
      '--dp-zoom-scale': 1,
      '--rte-zoom-scale': 1
    };
  };
  
  const pages = useMemo(() => {
    if (!renderedHtml) return [''];
    
    const pageBreakRegex = /<div\s+class=["']page-break["'][^>]*><\/div>/gi;
    const parts = renderedHtml.split(pageBreakRegex);
    
    if (parts.length === 1) {
      return [renderedHtml];
    }
    
    return parts.filter(part => part.trim().length > 0);
  }, [renderedHtml]);
  
  if (!content || content.trim() === '') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          İçerik bulunamadı. Önce şablon içeriğini oluşturun.
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box
      className={`document-preview-wrapper view-mode-${viewMode}`}
      style={getZoomStyle()}
    >
      <Box className="document-preview-zoom-container">
        <ZoomControlBar
          zoom={zoom}
          onZoomChange={handleZoomChange}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onActualSize={handleActualSize}
          viewMode={viewMode}
          minZoom={5}
          maxZoom={250}
          step={15}
        />
      </Box>

      <Box className="document-preview-content-wrapper">
        <Box className="document-preview-pages-container">
        {pages.map((pageHtml, index) => (
        <Paper
          key={index}
          elevation={0}
          className="document-preview-page"
          sx={{
            pageBreakAfter: index < pages.length - 1 ? 'always' : 'auto',
          }}
        >
          <div className="document-preview-page-wrapper">
            <div 
              className={`document-preview-content${isDraftMode ? ' draft-mode' : ''}${shouldBlockCopy ? ' protected' : ''}${shouldBlur ? ' blur-enabled' : ''}`}
              style={getZoomStyle()}
              dangerouslySetInnerHTML={{ __html: pageHtml }}
            />
          </div>
        </Paper>
        ))}
        </Box>
      </Box>

      <Popover
        open={popoverOpen}
        anchorReference="anchorPosition"
        anchorPosition={popoverAnchor ? { top: popoverAnchor.top, left: popoverAnchor.left } : undefined}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disableRestoreFocus
      >
        <Box sx={{ p: 1, display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenPage('yargitay')}
            sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5 }}
          >
            yargitay.gov.tr de Ara
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenPage('mevzuat')}
            sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5 }}
          >
            mevzuat.gov.tr de Ara
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

DocumentPreview.propTypes = {
  content: PropTypes.string,
  answers: PropTypes.object,
  questions: PropTypes.array,
  questionTree: PropTypes.array,
  onRender: PropTypes.func,
  status: PropTypes.oneOf(['draft', 'completed', 'signed', 'shared']),
  creditsUsed: PropTypes.number,
  skipApiCalls: PropTypes.bool,
  showOptionalPlaceholders: PropTypes.bool,
};

DocumentPreview.defaultProps = {
  content: '',
  answers: {},
  questions: [],
  questionTree: null,
  onRender: null,
  status: 'draft',
  creditsUsed: 0,
  skipApiCalls: false,
};

export default DocumentPreview;

